import { ArrowLeft, Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { type ReactNode } from "react";
import {
  useFormContext,
  type FieldValues,
  type Path,
  type SubmitErrorHandler,
  type SubmitHandler,
} from "react-hook-form";

import { Button } from "components/ui/button";

import { firstErrorMessage, warnStepBlocked } from "./form-errors";
import { SidePanel } from "./SidePanel";
import { WizardTabs } from "./WizardTabs";
import {
  getStepBlockReason,
  getStepFields,
  type Step,
  type StepGuardOptions,
  validateStepSemantics,
} from "./steps";

interface WizardSubmit<T extends FieldValues> {

  label: string;
  disabled: boolean;
  title?: string;
  onValid: SubmitHandler<T>;
  onInvalid: SubmitErrorHandler<T>;
}

interface GamificationWizardProps<T extends FieldValues> {
  title: string;
  headerBadge?: ReactNode;
  steps: Step[];
  currentStep: number;
  onStepChange: (step: number) => void;
  allowForwardNavigation?: boolean;

  reviewStep: number;
  guardOptions: StepGuardOptions;
  renderStep: (step: number) => ReactNode;
  onCancel: () => void;

  footerLeftExtra?: ReactNode;
  submit: WizardSubmit<T>;
}

export function GamificationWizard<T extends FieldValues>({
  title,
  headerBadge,
  steps,
  currentStep,
  onStepChange,
  allowForwardNavigation,
  reviewStep,
  guardOptions,
  renderStep,
  onCancel,
  footerLeftExtra,
  submit,
}: GamificationWizardProps<T>) {
  const form = useFormContext<T>();

  const goNext = async () => {
    const fields = getStepFields(currentStep) as unknown as Path<T>[];
    form.clearErrors(fields);
    const baseValid =
      fields.length === 0 || (await form.trigger(fields, { shouldFocus: true }));
    if (!baseValid) {
      warnStepBlocked(firstErrorMessage(form.formState.errors));
      return;
    }

    const semanticError = validateStepSemantics(currentStep, form.getValues());
    if (semanticError) {
      form.setError(semanticError.path as unknown as Path<T>, {
        type: "manual",
        message: semanticError.message,
      });
      warnStepBlocked(semanticError.message);
      return;
    }
    onStepChange(Math.min(currentStep + 1, reviewStep));
  };

  const goBack = () => onStepChange(Math.max(currentStep - 1, 1));

  const watchedValues = form.watch();
  const continueBlockReason = getStepBlockReason(
    currentStep,
    watchedValues,
    guardOptions,
  );

  const isReviewStep = currentStep === reviewStep;

  return (
    <div className="flex w-full items-stretch gap-6">
      <div className="min-w-0 flex-1">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              aria-label="Sair para a lista"
              onClick={onCancel}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="truncate text-2xl font-bold">{title}</h1>
            {headerBadge}
          </div>
          <span className="shrink-0 rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground">
            Passo {currentStep} de {steps.length}
          </span>
        </div>

        <WizardTabs
          steps={steps}
          currentStep={currentStep}
          onStepClick={onStepChange}
          allowForwardNavigation={allowForwardNavigation}
        />

        <form
          onSubmit={
            isReviewStep
              ? form.handleSubmit(submit.onValid, submit.onInvalid)
              : (event) => event.preventDefault()
          }
          className="w-full"
        >
          <div className="space-y-6">{renderStep(currentStep)}</div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" variant="ghost" onClick={onCancel}>
                <X className="mr-2 h-4 w-4" />
                Cancelar
              </Button>
              {footerLeftExtra}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                disabled={currentStep === 1}
                onClick={goBack}
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
              {!isReviewStep && (
                <Button
                  type="button"
                  disabled={!!continueBlockReason}
                  title={continueBlockReason ?? ""}
                  onClick={goNext}
                >
                  Continuar
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              )}
              {isReviewStep && (
                <Button
                  type="submit"

                  disabled={submit.disabled || form.formState.isSubmitting}
                  title={submit.title ?? ""}
                >
                  <Check className="mr-2 h-4 w-4" />
                  {submit.label}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>

      <aside className="hidden w-[300px] shrink-0 lg:block">
        <SidePanel />
      </aside>
    </div>
  );
}
